import RequestPermit from "../components/RequestPermit";
import { useRequestPermit } from "../operations/mutations/requestPermit";
import { useParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useLazyGetPermitToDig } from "../operations/queries/getPermitToDig";
import { triggerSavePermitted, triggerRequestPermitted } from "../models/Permit";
import { useRequestRejectedPermit } from "../operations/mutations/requestRejectedPermit";
import { PermitDataInput } from "../models/PermitDataInput";
import { useLazyGetAffectedServicesOptions } from "../operations/queries/getAffectedServicesOptions";
import { useLazyGetNextPermitNumber } from "../operations/queries/getNextPermitNumber";
import { EsriIntegrationTypes } from "../models/EsriIntegrationType";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { PermitAssignmentInput } from "../models/PermitAssignmentInput";
import { useGetRegisterAssignmentsByPermitRegisterId } from "../operations/queries/getRegisterAssignmentsByPermitRegisterId";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { useAddPermitRequest } from "../operations/mutations/addPermitRequest";
import { useUpdatePermit } from "../operations/mutations/updatePermit";
import { useAppContext } from "../hooks/useAppContext";
import { Boundary } from "../models/Boundary";
import { SitePhotoInput, PermitFieldValueInput } from "../models/globalTypes";
import { useLazyGetPermitTypeById } from "../operations/queries/getPermitTypeById";
import { useQueryString } from "../hooks/useQueryString";
import { mapBoundaryToAreaInput } from "../utils/boundaryUtils";

const RequestPermitContainer = () => {
  const { currentOrganization, currentProject, currentPermitRegister } = useAppContext();
  const { permitId } = useParams<{ permitId: string }>();
  const { sourceId } = useParams<{ sourceId: string }>();
  const queryString = useQueryString();
  const permitTypeId = queryString.get("permitTypeId") ?? undefined;
  const { authorizedToAddDocument } = usePermitRegisterPolicies();

  const { mutate: requestRejectedPermitMutate, status: requestRejectedPermitStatus } = useRequestRejectedPermit();
  const { getPermitToDig: getPermit, data: permit } = useLazyGetPermitToDig();
  const { getPermitToDig: getSourcePermit, data: sourcePermit } = useLazyGetPermitToDig();
  const { getRegisterAssignmentsByPermitRegisterId: getRegisterAssignments, data: registerAssignmentsData } =
    useGetRegisterAssignmentsByPermitRegisterId();
  const { getAffectedServicesOptions, data: affectedServicesOptions } = useLazyGetAffectedServicesOptions();
  const { mutate: requestPermitMutate, status: requestPermitStatus } = useRequestPermit();
  const { getNextPermitNumber, data: getNextPermitNumberData } = useLazyGetNextPermitNumber();
  const { mutate: addPermitRequestMutate, status: addPermitRequestStatus } = useAddPermitRequest();
  const { mutate: updatePermitMutate, status: updatePermitStatus } = useUpdatePermit();
  const { getPermitTypeById, data: permitTypeData } = useLazyGetPermitTypeById();

  const typeId = permit?.type?.id ?? sourcePermit?.type?.id ?? permitTypeId;

  const registerPermitTypeAssignments = useMemo(
    () =>
      registerAssignmentsData?.registerAssignmentsByPermitRegisterId?.filter(
        (ra) => ra.permitTypeOrgRole.permitType.id === typeId,
      ),
    [registerAssignmentsData, typeId],
  );

  useEffect(() => {
    if (typeId && currentOrganization) {
      getPermitTypeById({ variables: { organizationId: currentOrganization.id, permitTypeId: typeId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, currentOrganization]);

  useEffect(() => {
    const permitRegisterId = permit?.permitRegister?.id ?? currentPermitRegister?.id;
    if (permitRegisterId) {
      getRegisterAssignments({ variables: { permitRegisterId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit, currentPermitRegister]);

  useEffect(() => {
    if (currentPermitRegister && !permitId) {
      getNextPermitNumber({ variables: { permitRegisterId: currentPermitRegister.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPermitRegister, permitId]);

  useEffect(() => {
    getAffectedServicesOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (permitId) {
      getPermit({ variables: { id: permitId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitId]);

  useEffect(() => {
    if (sourceId) {
      getSourcePermit({ variables: { id: sourceId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const handleAddPermitRequest = (
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    sitePhotos: SitePhotoInput[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentPermitRegister && typeId) {
      addPermitRequestMutate({
        variables: {
          permitDataInput,
          number,
          permitRegisterId: currentPermitRegister.id,
          sourceId: sourcePermit ? sourcePermit.id : "",
          documentIds,
          calibrationCertificate,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          sitePhotos,
          permitTypeId: typeId,
          fieldValues,
        },
      });
    }
  };

  const handleEditPermit = (
    id: string,
    permitDataInput: PermitDataInput,
    documentIds: string[],
    calibrationCertificate: CalibrationCertificate,
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    sitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentProject) {
      updatePermitMutate({
        variables: {
          id,
          permitDataInput,
          documentIds,
          calibrationCertificate,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          sitePhotos,
          sitePhotoIds,
          fieldValues,
        },
      });
    }
  };

  const handleRequestPermit = (
    id: string,
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    sitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (currentPermitRegister && typeId) {
      requestPermitMutate({
        variables: {
          id,
          permitDataInput,
          number,
          permitRegisterId: currentPermitRegister.id,
          sourceId: sourcePermit ? sourcePermit.id : "",
          documentIds,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          calibrationCertificate,
          sitePhotos,
          sitePhotoIds,
          permitTypeId: typeId,
          fieldValues,
        },
      });
    }
  };

  const handleRequestRejectedPermit = (
    id: string,
    permitDataInput: PermitDataInput,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignmentInputs: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    sitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => {
    if (permit && currentPermitRegister && typeId) {
      requestRejectedPermitMutate({
        variables: {
          id,
          permitDataInput,
          documentIds,
          asBuiltRequired,
          areas: boundaries.map((b) => mapBoundaryToAreaInput(b)),
          permitAssignmentInputs,
          calibrationCertificate,
          sitePhotos,
          sitePhotoIds,
          permitRegisterId: currentPermitRegister.id,
          permitTypeId: typeId,
          fieldValues,
        },
      });
    }
  };

  return (
    <RequestPermit
      organizationHasEsriIntegration={
        currentOrganization?.esriConfig?.integrationTypeId != null &&
        currentOrganization.esriConfig.integrationTypeId !== EsriIntegrationTypes.NONE.id
      }
      saveButtonVisible={!permit || triggerSavePermitted(permit)}
      requestButtonVisible={!permit}
      requestRejectedPermitButtonsVisible={permit && triggerRequestPermitted(permit)}
      permit={permit}
      sourcePermit={sourcePermit}
      currentProject={currentProject}
      affectedServicesOptions={affectedServicesOptions && affectedServicesOptions.affectedServicesOptions}
      registerAssignments={registerPermitTypeAssignments}
      onAddPermitRequest={handleAddPermitRequest}
      addPermitRequestStatus={addPermitRequestStatus}
      onEditPermit={handleEditPermit}
      editPermitStatus={updatePermitStatus}
      onRequestPermit={handleRequestPermit}
      requestPermitStatus={requestPermitStatus}
      onRequestRejectedPermit={handleRequestRejectedPermit}
      requestRejectedPermitStatus={requestRejectedPermitStatus}
      nextPermitNumber={getNextPermitNumberData?.nextPermitNumber}
      addNewDocumentButtonVisible={authorizedToAddDocument}
      permitType={permitTypeData}
    ></RequestPermit>
  );
};

export default RequestPermitContainer;
